import { ASSETS, CONTACT } from "@/lib/constants";
import { PiggyBank, Sparkles, HandCoins, LineChart } from "lucide-react";
import { useMLO } from "@/lib/mloContext";

export default function WhyCheckRate() {
  const mlo = useMLO();
  const guidanceBody = mlo?.years_experience
    ? `You'll work directly with ${mlo.name} — ${mlo.years_experience}+ years of loan experience and no pressure tactics.`
    : `You'll work directly with ${mlo?.name || "a licensed loan officer at NEXA Lending"} — plain-English guidance and no pressure tactics.`;
  return (
    <section id="why" data-testid="why-section" className="section-y bg-linen relative">
      <div className="container-grc">
        <div className="max-w-3xl">
          <p className="text-xs font-bold tracking-[0.22em] uppercase text-teal">
            Why check your rate
          </p>
          <h2 className="mt-4 font-heading font-medium text-4xl sm:text-5xl text-balance">
            A few minutes today could mean{" "}
            <span className="italic text-teal">tens of thousands</span> in savings tomorrow.
          </h2>
          <p className="mt-5 text-lg text-ink-600 max-w-2xl">
            Whether you're buying your first home, refinancing, or investing —
            knowing where your rate stands is the fastest path to a smarter loan.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* Big image card */}
          <article
            data-testid="why-card-image"
            className="md:col-span-7 rounded-3xl overflow-hidden relative min-h-[340px] group"
          >
            <img
              src={ASSETS.familyMoving}
              alt="Family carrying moving boxes into a new home"
              className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy-deep/85 via-navy/40 to-transparent" />
            <div className="absolute inset-0 p-8 sm:p-10 flex flex-col justify-end text-white">
              <p className="text-xs font-bold tracking-[0.22em] uppercase text-lime">
                #1 reason
              </p>
              <h3 className="mt-3 font-heading font-medium text-3xl sm:text-4xl max-w-md">
                Real savings on the biggest loan of your life.
              </h3>
              <p className="mt-3 text-white/80 max-w-md">
                A quarter-point difference on a $450k mortgage can save you over
                $22,000 across the life of the loan. Small checks. Big outcome.
              </p>
            </div>
          </article>

          {/* Dark card */}
          <article
            data-testid="why-card-personalized"
            className="md:col-span-5 rounded-3xl bg-navy text-white p-8 sm:p-10 relative overflow-hidden"
          >
            <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-teal/20 blur-3xl" aria-hidden />
            <Sparkles className="text-lime" size={28} strokeWidth={1.75} />
            <h3 className="mt-6 font-heading font-medium text-2xl sm:text-3xl">
              Rates personalized to <em>you</em> — not the headline.
            </h3>
            <p className="mt-3 text-white/70">
              Advertised rates rarely tell the full story. We factor in your
              credit, down payment, property type and goals to show what's
              genuinely achievable.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-white/80">
              <li className="flex gap-2"><span className="text-lime">•</span> Purchase & refinance scenarios</li>
              <li className="flex gap-2"><span className="text-lime">•</span> Conventional, FHA, VA, Jumbo</li>
              <li className="flex gap-2"><span className="text-lime">•</span> Rate/APR breakdown, side-by-side</li>
            </ul>
          </article>

          {/* Standard cards */}
          <SmallCard
            testid="why-card-savings"
            icon={<PiggyBank size={26} strokeWidth={1.75} />}
            title="Cut your monthly payment"
            body="Even a modest refinance can free up hundreds a month. We'll show the break-even math clearly, before you commit."
          />
          <SmallCard
            testid="why-card-guidance"
            icon={<HandCoins size={26} strokeWidth={1.75} />}
            title="A real human, not a robo-quote"
            body={guidanceBody}
          />
          <SmallCard
            testid="why-card-market"
            icon={<LineChart size={26} strokeWidth={1.75} />}
            title="Move with market timing"
            body="We monitor rate movement daily and reach out when a lock is worth considering. No spam, ever."
          />
        </div>
      </div>
    </section>
  );
}

function SmallCard({ icon, title, body, testid }) {
  return (
    <article
      data-testid={testid}
      className="md:col-span-4 rounded-3xl bg-white border border-linen-border/70 p-8 shadow-soft hover:shadow-lift transition-shadow duration-300"
    >
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-teal-light text-teal">
        {icon}
      </div>
      <h3 className="mt-5 font-heading font-medium text-xl">{title}</h3>
      <p className="mt-2 text-ink-600 leading-relaxed">{body}</p>
    </article>
  );
}
